/**
 * <bundle-preset-tabs> — Roving-tabindex tab bar for switching between
 * bundle presets (essential / complete). Dispatches `bundle:apply-preset`
 * and listens for `bundle:preset-applied` to sync visual state when
 * presets are applied externally (e.g. from URL params).
 */
class BundlePresetTabs extends HTMLElement {
  #controller;
  #tabs;

  connectedCallback() {
    this.#controller = new AbortController();
    const opts = { signal: this.#controller.signal };

    this.#tabs = [...this.querySelectorAll('[data-preset]')];

    for (const tab of this.#tabs) {
      tab.addEventListener('click', (e) => this.#handleTabClick(e), opts);
    }

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

    document.addEventListener('bundle:preset-applied', (e) => {
      const target = this.#tabs.find((t) => t.dataset.preset === e.detail.preset);
      if (target) this.#selectTab(target);
    }, opts);
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #handleTabClick(event) {
    const tab = event.currentTarget;
    const preset = tab.dataset.preset;

    this.#selectTab(tab);

    document.dispatchEvent(new CustomEvent('bundle:apply-preset', {
      detail: { preset },
    }));
  }

  #handleKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();

    const currentIndex = this.#tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    const nextIndex = event.key === 'ArrowRight'
      ? (currentIndex + 1) % this.#tabs.length
      : (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;

    const nextTab = this.#tabs[nextIndex];
    this.#selectTab(nextTab);
    nextTab.focus();

    document.dispatchEvent(new CustomEvent('bundle:apply-preset', {
      detail: { preset: nextTab.dataset.preset },
    }));
  }

  #selectTab(activeTab) {
    for (const tab of this.#tabs) {
      const isSelected = tab === activeTab;
      tab.classList.toggle('bundle-preset-tabs__tab--selected', isSelected);
      tab.setAttribute('aria-selected', String(isSelected));
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
    }
  }
}

if (!customElements.get('bundle-preset-tabs')) {
  customElements.define('bundle-preset-tabs', BundlePresetTabs);
}
